import React, { useEffect, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Colors } from 'library';
import { Card, ErrorText, FieldGroup, ModalFooter, PillButton, Stack, Text, TextInput } from 'library/ui';
import { createSetupIntent, savePaymentMethod } from './api';
import { readPaymentMethodId } from './stripeHelpers';
import { PaymentMethod, PaymentProduct } from './types';

interface AddCardFormProps {
    onSuccess: (method: PaymentMethod) => void;
    onCancel?: () => void;
    submitLabel?: string;
    cancelLabel?: string;
    product?: PaymentProduct;
}

export function AddCardForm({
    onSuccess,
    onCancel,
    submitLabel = 'Add Card',
    cancelLabel = 'Cancel',
    product = 'subs',
}: AddCardFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [cardholderName, setCardholderName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cardComplete, setCardComplete] = useState(false);

    useEffect(() => {
        elements?.getElement(CardElement)?.on('change', function (event) {
            setCardComplete(event.complete);
            if (event.error) {
                setError(event.error.message);
            } else {
                setError(null);
            }
        });
    }, [elements]);

    async function saveCard() {
        if (!stripe || !elements) return;
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) return;

        setSubmitting(true);
        setError(null);

        try {
            const { client_secret } = await createSetupIntent('card');
            const result = await stripe.confirmCardSetup(client_secret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { name: cardholderName || undefined },
                },
            });
            if (result.error) {
                setError(result.error.message ?? 'Could not save card.');
                setSubmitting(false);
                return;
            }
            const paymentMethodId = readPaymentMethodId(result);
            if (!paymentMethodId) {
                setError('Stripe did not return a payment method id.');
                setSubmitting(false);
                return;
            }
            const saved = await savePaymentMethod(paymentMethodId, result.setupIntent?.status, product);
            setSubmitting(false);
            onSuccess(saved);
        } catch {
            setError('Could not save card. Please try again.');
            setSubmitting(false);
        }
    }

    return (
        <Stack gap="md">
            <FieldGroup label="Cardholder Name">
                <TextInput value={cardholderName} onChange={setCardholderName} placeholder="Name on card" fullWidth />
            </FieldGroup>
            <FieldGroup label="Card Details">
                <Card tone="muted" padding="xs">
                    <CardElement
                        options={{
                            hidePostalCode: false,
                            style: {
                                base: {
                                    fontSize: '15px',
                                    color: Colors.primary,
                                    '::placeholder': { color: Colors.textSecondary },
                                },
                                invalid: { color: Colors.error },
                            },
                        }}
                    />
                </Card>
            </FieldGroup>
            {error && <ErrorText>{error}</ErrorText>}
            {!stripe && <Text tone="secondary">Loading Stripe…</Text>}
            <ModalFooter>
                {onCancel && (
                    <PillButton variant="secondary" onClick={onCancel} disabled={submitting}>
                        {cancelLabel}
                    </PillButton>
                )}
                <PillButton
                    variant="primary"
                    onClick={saveCard}
                    disabled={!stripe || submitting || !cardholderName || !cardComplete || !!error}
                >
                    {submitting ? 'Saving…' : submitLabel}
                </PillButton>
            </ModalFooter>
        </Stack>
    );
}
